import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "./api";

// The web console's profile read/write for Settings scalars (units, effort,
// sex, age, …). The API's PUT REMOVEs optional fields absent from the body
// (profiles.ts setIfPresent), so every save is a get-merge-PUT of the whole
// doc — the same round-trip mobile's patchProfile does. There's no offline
// cache here (the console is online); the mobile ProfileContext keeps its
// own AsyncStorage warm-cache.

type ProfileDoc = Record<string, unknown>;

export type UseProfileDoc = {
  doc: ProfileDoc | null;
  loading: boolean;
  error: string | null;
  /** get-merge-PUT a single field, then refresh the local doc. Throws on failure. */
  saveField: (field: string, value: unknown) => Promise<void>;
  /** get-merge-PUT several fields at once (e.g. sex + seeded figure). Throws on failure. */
  savePatch: (patch: Record<string, unknown>) => Promise<void>;
  /** Re-pull the doc with no write (RW-18's LoggerHostDeps.refreshProfile —
   *  the MAX-bump / energy-readout paths need the freshest snapshot after a
   *  save, without a pointless get-merge-PUT-of-itself). */
  refresh: () => Promise<void>;
};

// PROF-1 — un 404 sulla GET del profilo vuol dire «documento VUOTO», non
// «errore». Chi entra su invito non ha ancora una riga PROFILE (misurato
// l'1/09: la GET risponde 404 `not_found`), e trattare quella risposta come un
// guasto rendeva impossibile la PRIMA scrittura — cioè esattamente quella che
// avrebbe creato la riga. La PUT lato API è create-or-update, quindi fondere
// sul vuoto è la cosa giusta.
//
// ⚠️ Questo NON basta da solo, e vale la pena scriverlo: la PUT valida
// `name`, che è l'unico campo dello schema senza default. Un patch che non lo
// porta, su un documento vuoto, prende un 400 invece di un 404 — meglio, ma
// non «funziona». È lo schermo di benvenuto (PROF-3) a garantire che il nome
// ci sia dal primo ingresso; questa è la rete sotto, non il pavimento.
const EMPTY_DOC: ProfileDoc = {};

const readDocOrEmpty = async (): Promise<ProfileDoc> => {
  try {
    return await apiFetch<ProfileDoc>("/api/profiles");
  } catch (err) {
    if ((err as ApiError).status === 404) return EMPTY_DOC;
    throw err;
  }
};

export function useProfileDoc(): UseProfileDoc {
  const [doc, setDoc] = useState<ProfileDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDoc(await readDocOrEmpty());
      setError(null);
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const savePatch = useCallback(async (patch: Record<string, unknown>) => {
    const current = await readDocOrEmpty();
    const next = { ...current, ...patch };
    await apiFetch<unknown>("/api/profiles", { method: "PUT", body: next });
    setDoc(next);
  }, []);

  const saveField = useCallback(
    (field: string, value: unknown) => savePatch({ [field]: value }),
    [savePatch],
  );

  return {
    doc,
    loading: doc === null && error === null,
    error,
    saveField,
    savePatch,
    refresh: load,
  };
}
